/**
 * Зал ожидания ведущего: крупный PIN + QR-код входа + счётчик участников +
 * опция «Скрывать счёт после каждого вопроса» (инвертированная галка scorePause,
 * И4; решение 2026-11-02) + кнопка «Начать игру» (команда `start` на сервер).
 * QR кодирует URL главной с предзаполненным PIN (`/?pin=<pin>`).
 */
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import { buildJoinUrl, formatPin } from './ui-helpers';
import { useEffect, useState } from 'react';

export interface PinLobbyProps {
  pin: string;
  /** Текущее число участников комнаты (state.participants / scoreboard.entries.length). */
  participants: number;
  /** Команда хоста «Начать игру». */
  onStart: () => void;
  /** Игра уже стартовала — кнопку блокируем. */
  started?: boolean;
  /** Текст ошибки (например, сервер не принял start). */
  error?: string | null;
  /** Опция «показывать счёт после каждого вопроса» (И4): wire-семантика
   *  (true = показывать). UI-галка с 2026-11-02 инвертирована в «Скрывать
   *  счёт после каждого вопроса»: checked = !scorePause. Дефолт — true. */
  scorePause?: boolean;
  /** Опция «счёт после каждого вопроса» (И4): смена значения (колбэк получает
   *  wire-значение scorePause, НЕ состояние галки). Чекбокс рендерится только
   *  при переданном обработчике. */
  onScorePauseChange?: (v: boolean) => void;
  /** «Авто-переход к следующему вопросу» (Фаза 16, A): текущее значение чекбокса. */
  autoNext?: boolean;
  /** «Авто-переход к следующему вопросу» (Фаза 16, A): смена значения. Чекбокс
   *  рендерится только при переданном обработчике. */
  onAutoNextChange?: (v: boolean) => void;
  /** «Закрыть вход после старта» (Фаза 13, C1): текущее значение чекбокса. */
  lockAfterStart?: boolean;
  /** «Закрыть вход после старта» (Фаза 13, C1): смена значения. Чекбокс
   *  рендерится только при переданном обработчике. */
  onLockAfterStartChange?: (v: boolean) => void;
  /** «Строгие ники» (Фаза 13, C1): текущее значение чекбокса. Дефолт — true
   *  (решение 2026-11-02): один игрок на ник, если хост не снял галку. */
  strictNick?: boolean;
  /** «Строгие ники» (Фаза 13, C1): смена значения. Чекбокс рендерится только
   *  при переданном обработчике. */
  onStrictNickChange?: (v: boolean) => void;
}

// Статус WS больше не дублируется здесь — он уже показан в шапке HostSession
// (StatusPill в .screen-head-meta), что убирает двойной «подключено/подключается».
export function PinLobby({
  pin,
  participants,
  onStart,
  started = false,
  error = null,
  scorePause = true,
  onScorePauseChange,
  autoNext = false,
  onAutoNextChange,
  lockAfterStart = false,
  onLockAfterStartChange,
  strictNick = true,
  onStrictNickChange,
}: PinLobbyProps): JSX.Element {
  const { t } = useTranslation();
  const joinUrl = buildJoinUrl(pin);

  // Таймер обратного отсчёта (20 минут = 1200 секунд)
  const [timeLeft, setTimeLeft] = useState(20 * 60);

  useEffect(() => {
    if (started) return; // Не запускаем таймер если игра уже началась

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [started]);

  // Форматирование времени в ММ:СС
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="lobby">
      <div className="lobby-head">
        <h2 className="lobby-title">{t('lobby.title')}</h2>
      </div>
      <p className="muted">{t('lobby.hint')}</p>

      <div className="qr-wrap" aria-label={t('lobby.qrAria', { url: joinUrl })}>
        <QRCodeSVG value={joinUrl} size={196} level="M" marginSize={4} />
      </div>

      <div className="pin-big" aria-label={t('lobby.pinAria', { pin })}>
        {formatPin(pin)}
      </div>

      <div className="participants">
        {t('lobby.participants')} <strong data-testid="participants-count">{participants}</strong>
      </div>

      {/* Предупреждение о времени ожидании */}
      <div className="lobby-warning" role="alert">
        ⏱️ <strong>{t('lobby.warningTitle')}</strong> {t('lobby.warningText')}
      </div>

      {/* Таймер обратного отсчёта */}
      {!started && (
        <div className="lobby-timer" data-testid="lobby-timer">
          🕐 {t('lobby.timerLabel')}: <strong>{formatTime(timeLeft)}</strong>
        </div>
      )}

      {/* И4 (Фаза 10): опция «показывать счёт после каждого вопроса» —
          фиксируется при start и не меняется по ходу игры. Решение
          2026-11-02: галка инвертирована в «Скрывать счёт после каждого
          вопроса» и по умолчанию снята → scorePause=true (счёт показывается). */}
      {onScorePauseChange ? (
        <>
          <label className="lobby-option">
            <input
              type="checkbox"
              checked={!scorePause}
              onChange={(e) => onScorePauseChange(!e.target.checked)}
              data-testid="lobby-score-pause"
            />
            {t('lobby.scorePause')}
          </label>
          <p className="muted small">{t('lobby.scorePauseHint')}</p>
        </>
      ) : null}

      {/* Фаза 16 (A): авто-переход к следующему вопросу после reveal —
          серверный таймер (задержка из админ-настройки, фиксируется при
          start) сам выполняет «дальше»; ручной клик опережает таймер. */}
      {onAutoNextChange ? (
        <label className="lobby-option">
          <input
            type="checkbox"
            checked={autoNext}
            onChange={(e) => onAutoNextChange(e.target.checked)}
            data-testid="lobby-auto-next"
          />
          {t('lobby.autoNext')}
        </label>
      ) : null}

      {/* Фаза 13 (C2): настройки входа/идентичности — фиксируются при start,
          предзаполнены выбором модала «Провести» (кабинет /me). */}
      {onLockAfterStartChange ? (
        <label className="lobby-option">
          <input
            type="checkbox"
            checked={lockAfterStart}
            onChange={(e) => onLockAfterStartChange(e.target.checked)}
            data-testid="lobby-lock-after-start"
          />
          {t('lobby.lockAfterStart')}
        </label>
      ) : null}
      {onStrictNickChange ? (
        <label className="lobby-option">
          <input
            type="checkbox"
            checked={strictNick}
            onChange={(e) => onStrictNickChange(e.target.checked)}
            data-testid="lobby-strict-nick"
          />
          {t('lobby.strictNick')}
        </label>
      ) : null}

      <button type="button" className="cta" onClick={onStart} disabled={started}>
        {started ? t('lobby.inProgress') : t('lobby.start')}
      </button>

      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
